import React, { useState, useEffect } from 'react';
import Icon from '../../../components/AppIcon';
import { articlesService, projectsService, subUnionsService } from '../../../services/dataService';
import { formatTimeAgo } from '../../../utils/dateUtils';

const ActivityFeed = () => {
  const [activities, setActivities] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    const fetchActivities = async () => {
      try {
        setLoading(true);
        
        // Fetch recent data to generate activity feed
        const [articles, projects, subUnions] = await Promise.all([
          articlesService?.getAll()?.catch(() => []),
          projectsService?.getAll()?.catch(() => []),
          subUnionsService?.getAll()?.catch(() => [])
        ]);

        // Generate activity items from the data
        const activityItems = [];
        
        // Add recent articles
        articles?.slice(0, 3)?.forEach(article => {
          activityItems?.push({
            id: `article-${article?.id}`,
            type: 'article_created',
            title: 'تم إنشاء مقال جديد',
            description: article?.titleAr || article?.titleEn || 'مقال بدون عنوان',
            author: article?.author?.fullName || 'مؤلف غير معروف',
            time: article?.createdAt,
            icon: 'FileText',
            iconColor: 'text-blue-500',
            iconBg: 'bg-blue-500/10'
          });
        });

        // Add recent projects
        projects?.slice(0, 3)?.forEach(project => {
          activityItems?.push({
            id: `project-${project?.id}`,
            type: 'project_updated',
            title: 'تم تحديث مشروع',
            description: project?.titleAr || project?.titleEn || 'مشروع بدون عنوان',
            author: 'فريق المشاريع',
            time: project?.updatedAt || project?.createdAt,
            icon: 'FolderOpen',
            iconColor: 'text-green-500',
            iconBg: 'bg-green-500/10'
          });
        });

        // Add recent sub unions
        subUnions?.slice(0, 2)?.forEach(union => {
          activityItems?.push({
            id: `union-${union?.id}`,
            type: 'union_registered',
            title: 'تم تسجيل اتحاد فرعي جديد',
            description: union?.nameAr || union?.nameEn || 'اتحاد بدون اسم',
            author: 'إدارة الاتحادات',
            time: union?.createdAt,
            icon: 'Building2',
            iconColor: 'text-purple-500',
            iconBg: 'bg-purple-500/10'
          });
        });

        // Sort by time (newest first) and take top 10
        const sortedActivities = activityItems?.sort((a, b) => new Date(b.time) - new Date(a.time))?.slice(0, 10);

        setActivities(sortedActivities);
      } catch (err) {
        console.error('Error fetching activities:', err);
        setError('فشل في تحميل النشاطات الأخيرة');
      } finally {
        setLoading(false);
      }
    };

    fetchActivities();
  }, []);


  if (loading) {
    return (
      <div className="bg-card border border-border rounded-xl p-6 shadow-elevation-1 h-fit">
        <div className="flex items-center justify-between mb-6">
          <h3 className="text-lg font-semibold text-foreground font-arabic">
            النشاطات الأخيرة
          </h3>
          <Icon name="Activity" size={20} className="text-muted-foreground" />
        </div>
        <div className="space-y-4">
          {[...Array(6)]?.map((_, index) => (
            <div key={index} className="animate-pulse">
              <div className="flex items-start space-x-3 rtl:space-x-reverse">
                <div className="w-10 h-10 bg-muted rounded-lg"></div>
                <div className="flex-1 space-y-2">
                  <div className="h-4 bg-muted rounded w-3/4"></div>
                  <div className="h-3 bg-muted rounded w-1/2"></div>
                  <div className="h-3 bg-muted rounded w-1/4"></div>
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="bg-card border border-border rounded-xl p-6 shadow-elevation-1 h-fit">
        <div className="text-center py-8">
          <Icon name="AlertCircle" size={48} className="text-error mx-auto mb-4" />
          <p className="text-error font-medium mb-2">{error}</p>
          <button 
            onClick={() => window.location?.reload()} 
            className="text-sm text-primary hover:text-primary/80 transition-colors"
          >
            إعادة المحاولة
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="bg-card border border-border rounded-xl p-6 shadow-elevation-1 h-fit">
      <div className="flex items-center justify-between mb-6">
        <h3 className="text-lg font-semibold text-foreground font-arabic">
          النشاطات الأخيرة
        </h3>
        <Icon name="Activity" size={20} className="text-muted-foreground" />
      </div>
      {activities?.length === 0 ? (
        <div className="text-center py-8">
          <Icon name="Calendar" size={48} className="text-muted-foreground mx-auto mb-4" />
          <p className="text-muted-foreground">لا توجد نشاطات حديثة</p>
        </div>
      ) : (
        <div className="space-y-4 max-h-96 overflow-y-auto">
          {activities?.map((activity, index) => (
            <div key={activity?.id} className="relative">
              {/* Timeline line */}
              {index < activities?.length - 1 && (
                <div className="absolute right-5 top-12 w-px h-8 bg-border"></div>
              )}
              
              <div className="flex items-start space-x-3 rtl:space-x-reverse">
                <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${activity?.iconBg}`}>
                  <Icon name={activity?.icon} size={18} className={activity?.iconColor} />
                </div>
                
                <div className="flex-1 min-w-0">
                  <div className="flex items-start justify-between mb-1">
                    <p className="font-medium text-foreground font-arabic text-sm">
                      {activity?.title}
                    </p>
                    <span className="text-xs text-muted-foreground font-caption whitespace-nowrap mr-2">
                      {formatTimeAgo(activity?.time)}
                    </span>
                  </div>
                  
                  <p className="text-sm text-muted-foreground font-caption line-clamp-2 mb-2">
                    {activity?.description}
                  </p>
                  
                  <div className="flex items-center space-x-2 rtl:space-x-reverse">
                    <Icon name="User" size={12} className="text-muted-foreground" />
                    <span className="text-xs text-muted-foreground font-caption">
                      بواسطة: {activity?.author}
                    </span>
                  </div>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}
      <div className="mt-6 pt-4 border-t border-border">
        <button className="text-sm text-primary hover:text-primary/80 transition-colors font-medium">
          عرض جميع النشاطات
        </button>
      </div>
    </div>
  );
};

export default ActivityFeed;